import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Heart, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../hooks/useSEO';

const CONTACT_ENDPOINT = 'https://formbold.com/s/3OOwO';

export default function About() {
  useSEO({
    title: 'About Noax Media | 11+ Years Behind Every Real-Time Lead',
    description: 'Noax Media is led by 11+ years of hands-on experience in real-time US lead generation for Medicare Leads, Final Expense Leads, and home services. Built by operators, not marketers.',
    path: '/about',
  });
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    email: '',
    phone: '',
    vertical: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const values = [
    {
      icon: Award,
      title: 'Quality Over Quantity',
      description: 'We measure success by your close rate, not our lead count.',
    },
    {
      icon: Heart,
      title: 'Real Transparency',
      description: 'No recycled data. No hidden fees. No empty promises.',
    },
    {
      icon: TrendingUp,
      title: 'Client Growth First',
      description: 'Your revenue growth is our primary success metric.',
    },
    {
      icon: Users,
      title: 'Constant Innovation',
      description: 'We evolve our systems as markets and technology change.',
    },
  ];

  const verticals = [
    'Solar', 'Roofing', 'Home Improvement', 'Real Estate', 'Medicare',
    'Final Expense', 'Life Insurance', 'ACA', 'Auto Insurance', 'Truck Insurance', 'Other',
  ];

  const services = [
    'Real-Time Leads', 'Data Solutions', 'Live Transfers',
    'Social Media Marketing', 'Social Media Management', 'AI Automations',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // FormBold requires the 4 exact keys: name, email, phone, message.
    // Fold the extra UI fields (business_name / vertical / service) into
    // the message body so nothing is lost.
    const extraLines = [];
    if (formData.business_name) extraLines.push(`Business: ${formData.business_name}`);
    if (formData.vertical)      extraLines.push(`Vertical: ${formData.vertical}`);
    if (formData.service)       extraLines.push(`Service:  ${formData.service}`);
    const messageBody = extraLines.length
      ? `${extraLines.join('\n')}\n\n${formData.message}`
      : formData.message;

    const payload = {
      name:    formData.full_name,
      email:   formData.email,
      phone:   formData.phone,
      message: messageBody,
    };

    try {
      await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Per the requirement, show success immediately after submission.
      toast.success('Thank you! We will be in touch soon.');
      setFormData({
        full_name: '', business_name: '', email: '', phone: '',
        vertical: '', service: '', message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1420] text-white overflow-hidden">
      {/* Animated Background Layer - Global */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#31638C] opacity-[0.04] rounded-full blur-3xl animate-drift"></div>
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.03] rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-[#31638C] opacity-[0.04] rounded-full blur-3xl animate-drift" style={{ animationDelay: '7s' }}></div>
      </div>

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 lg:pt-36 lg:pb-24 grain-overlay"
        data-testid="about-hero"
      >
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#31638C] opacity-15 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute top-40 right-1/4 w-96 h-96 bg-[#4A8BC2] opacity-10 rounded-full blur-3xl animate-drift"></div>
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#31638C]/10 border border-[#31638C]/30 mb-6">
              <span className="text-xs font-medium text-[#31638C] tracking-wider uppercase">About Us</span>
            </div>
            <h1 
              className="text-5xl lg:text-7xl font-bold tracking-tighter leading-none mb-6"
              data-testid="about-heading"
            >
              Experience You<br />
              <span className="gradient-text">Can not Fake</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto">
              Noax Media may be a young company — but it is led by 11 years of real industry experience 
              and two businesses built from the ground up.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 relative" data-testid="our-story">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#31638C] opacity-5 rounded-full blur-3xl animate-drift"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-medium">OUR STORY</span>
              <h2 
                className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-8"
              >
                From the Ground Floor to the<br />
                <span className="gradient-text">Front of the Industry</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-400 leading-relaxed">
                <p>
                  Noax Media is a startup with experienced leadership. Our founder brings 11 years of hands-on 
                  experience in lead generation and performance marketing, including building two successful 
                  businesses from scratch.
                </p>
                <p>
                  We saw too many businesses being sold recycled leads and empty promises. We knew we could do 
                  it better — with real-time delivery, genuine expertise, and a relentless focus on results.
                </p>
                <p className="font-medium text-[#31638C] text-xl">
                  That is why we built Noax Media.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-radial from-[#31638C]/25 via-transparent to-transparent blur-3xl"></div>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                  alt="Noax Media operators — 11+ years generating exclusive Medicare leads, final expense leads, and real-time home services leads"
                  className="w-full"
                  style={{
                    filter: 'brightness(0.55) contrast(1.1) saturate(0.75)',
                    WebkitMaskImage: 'radial-gradient(ellipse 85% 85% at center, black 45%, transparent 100%)',
                    maskImage: 'radial-gradient(ellipse 85% 85% at center, black 45%, transparent 100%)',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1420]/40 via-transparent to-[#0A1420]/60 mix-blend-multiply pointer-events-none"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 lg:py-28 bg-[#131E2C] relative overflow-hidden" data-testid="our-mission">
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-medium">OUR MISSION</span>
            <h2 
              className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-6"
            >
              What <span className="gradient-text">Drives Us</span>
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              To become the most trusted real-time lead partner in the home and insurance industries — 
              by delivering leads that actually convert, backed by service that treats your growth like our own.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-[#131E2C] p-8 rounded-xl border border-[#5D676E]/25 card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`value-${index}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#31638C]/20 to-[#4A8BC2]/20 border border-[#31638C]/30 flex items-center justify-center mb-4">
                    <Icon className="text-[#31638C]" size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">
                    {value.title}
                  </h3>
                  <p className="text-gray-400">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="py-20 lg:py-28 relative overflow-hidden" data-testid="why-trust">
        <div className="absolute inset-0 bg-gradient-to-br from-[#31638C]/10 to-transparent"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#4A8BC2] opacity-10 rounded-full blur-3xl animate-drift"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-radial from-[#31638C]/25 via-transparent to-transparent blur-3xl"></div>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1552581234-26160f608093?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                  alt="Noax Media strategy meeting — trusted real-time lead generation partner for Medicare, final expense, insurance, and home services agents"
                  className="w-full"
                  style={{
                    filter: 'brightness(0.55) contrast(1.1) saturate(0.75)',
                    WebkitMaskImage: 'radial-gradient(ellipse 85% 85% at center, black 45%, transparent 100%)',
                    maskImage: 'radial-gradient(ellipse 85% 85% at center, black 45%, transparent 100%)',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1420]/40 via-transparent to-[#0A1420]/60 mix-blend-multiply pointer-events-none"></div>
              </div>
            </motion.div>

            <div>
              <motion.h2 
                className="text-4xl lg:text-5xl font-bold tracking-tight mb-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                Why Businesses<br />
                <span className="gradient-text">Trust Noax Media</span>
              </motion.h2>
              <div className="space-y-4">
                {[
                  'Founded on 11 years of real, in-the-field experience',
                  'Track record of building businesses, not just running campaigns',
                  'Real-time lead technology across multiple digital platforms',
                  'Deep specialization in home and insurance verticals',
                  'A partner mindset — invested in your close rate',
                ].map((item, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start bg-[#131E2C] backdrop-blur-sm p-4 rounded-lg border border-[#5D676E]/25 hover:border-[#31638C]/40 transition-colors"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    data-testid={`trust-point-${index}`}
                  >
                    <ArrowRight className="text-[#31638C] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span className="text-lg text-gray-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-28 bg-[#131E2C] relative overflow-hidden" data-testid="contact-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#31638C] opacity-5 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-medium">GET IN TOUCH</span>
            <h2 
              className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-4"
            >
              Let us Build Your<br />
              <span className="gradient-text">Pipeline</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Ready to discuss your goals and see what real-time, high-intent leads can do for your business?
            </p>
          </motion.div>

          <motion.form 
            onSubmit={handleSubmit} 
            className="max-w-3xl mx-auto bg-[#131E2C] p-8 lg:p-12 rounded-2xl border border-[#5D676E]/25"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all placeholder-gray-500"
                  data-testid="input-full-name"
                />
              </div>

              <div>
                <label htmlFor="business_name" className="block text-sm font-medium text-gray-300 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  id="business_name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all placeholder-gray-500"
                  data-testid="input-business-name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all placeholder-gray-500"
                  data-testid="input-email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all placeholder-gray-500"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <label htmlFor="vertical" className="block text-sm font-medium text-gray-300 mb-2">
                  Vertical / Industry *
                </label>
                <select
                  id="vertical"
                  name="vertical"
                  value={formData.vertical}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all"
                  data-testid="select-vertical"
                >
                  <option value="">Select vertical...</option>
                  {verticals.map((vertical) => (
                    <option key={vertical} value={vertical}>{vertical}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all"
                  data-testid="select-service"
                >
                  <option value="">Select service...</option>
                  {services.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 bg-[#0A1420] border border-[#5D676E]/25 text-white rounded-lg focus:ring-2 focus:ring-[#31638C] focus:border-transparent transition-all placeholder-gray-500"
                data-testid="textarea-message"
              />
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-[#31638C] to-[#4A8BC2] text-white font-semibold rounded-lg hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send My Inquiry'}
              </button>
            </div>
          </motion.form>

          <div className="mt-12 text-center text-gray-400">
            <p className="mb-2">Or reach us directly:</p>
            <p>Email: <a href="mailto:info@noaxmedia.com" className="text-[#31638C] hover:underline">info@noaxmedia.com</a></p>
            <p>Phone: <a href="tel:+17206049950" className="text-[#31638C] hover:underline">+1 (720) 604-9950</a></p>
          </div>
        </div>
      </section>

      {/* Final CTA Band */}
      <section className="py-16 relative overflow-hidden text-center" data-testid="final-cta-band">
        <div className="absolute inset-0 bg-gradient-to-br from-[#31638C] to-[#1E4A8C]"></div>
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        
        <div className="relative max-w-4xl mx-auto px-6">
          <h2 
            className="text-3xl lg:text-4xl font-bold tracking-tight text-white"
          >
            Your Next Customer Is Already Searching.<br />
            Let us Get You in Front of Them.
          </h2>
        </div>
      </section>
    </div>
  );
}
